import {
    FileTextOutlined,
    FileExcelOutlined,
    CalendarOutlined,
    DownloadOutlined,
    ReloadOutlined,
    EyeOutlined,
    BankOutlined,
    FilePdfOutlined,
    CheckOutlined,
    TableOutlined
} from '@ant-design/icons';
import {
    ConfigProvider,
    DatePicker,
    Select,
    Card,
    Table,
    Tag,
    Space,
    Button,
    Row,
    Col,
    Statistic,
    Tooltip,
    Modal,
    Descriptions,
    Divider,
    Tabs
} from 'antd';
import frFR from "antd/locale/fr_FR";
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import axiosInstance from '../../../variables/api/axiosInstance';
import { LOGGED_USER } from '../../../variables/constants';
import { frenchMonth } from '../../../variables/functions';
import { displayNumbers } from '../../../variables/numbersFunctions';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import TableauRecapitulatifRetrocession from './components/TableauRecapitulatifRetrocession';
import BordereauxDetailles from './components/BordereauxDetailles';
import { ChequeRetrocession } from './components/ChequeRetrocession';

dayjs.locale('fr');

const Retrocessions = () => {
    const [retrocessions, setRetrocessions] = useState([]);
    const [branches, setBranches] = useState([]);
    const [selectedBranche, setSelectedBranche] = useState(null);
    const [compagnies, setCompagnies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPeriode, setSelectedPeriode] = useState(null);
    const [selectedCompagnie, setSelectedCompagnie] = useState(null);
    const [retrocessionsGroupees, setRetrocessionsGroupees] = useState([]);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [groupModalVisible, setGroupModalVisible] = useState(false);
    const [selectedRetrocession, setSelectedRetrocession] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState({});

    const navigate = useNavigate();

    const isPoolUser = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";

    useEffect(() => {
        chargerDonneesInitiales();
    }, []);

    useEffect(() => {
        chargerRetrocessions();
    }, [selectedPeriode, selectedBranche, selectedCompagnie]);

    useEffect(() => {
        if (selectedGroup?.key) {
            const updatedGroup = retrocessionsGroupees.find(g => g.key === selectedGroup.key);
            if (updatedGroup) {
                setSelectedGroup(updatedGroup);
            }
        }
    }, [retrocessionsGroupees]);

    const chargerDonneesInitiales = async () => {
        try {
            const [catRes, brancheRes, compRes] = await Promise.all([
                axiosInstance.get('/api/categorie'),
                axiosInstance.get('/api/branche'),
                axiosInstance.get('/api/organisme/assurance')
            ]);

            setBranches(brancheRes.data);

            const excludedCodes = ["A", "B", "T"];
            const filtered = catRes.data.filter(
                (c) => !c.codeCategorie.startsWith("V") && !excludedCodes.includes(c.codeCategorie)
            );

            const hasTPVCategories = catRes.data.some((c) => excludedCodes.includes(c.codeCategorie));
            if (hasTPVCategories) {
                filtered.push({ codeCategorie: "TPV", nom: "TPV", synthetic: true });
            }

            // setCategories(filtered);
            setCompagnies(compRes.data);
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des données");
        }
    };

    const chargerRetrocessions = async () => {
        setLoading(true);
        try {
            const params = {};
            if (selectedPeriode) params.mois = selectedPeriode + "-01";
            if (selectedBranche) params.branche = selectedBranche;
            if (selectedCompagnie) params.compagnie = selectedCompagnie;

            const response = await axiosInstance.get('/api/retrocession', { params });
            setRetrocessions(response.data);
            console.log("Retrocessionnnnnnnns: ", response.data);
            const groupees = grouperRetrocessions(response.data);
            setRetrocessionsGroupees(groupees);
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des rétrocessions");
        } finally {
            setLoading(false);
        }
    };

    const grouperRetrocessions = (retrocessions) => {
        const groupes = {};

        retrocessions.forEach((retro) => {
            const moisRapport = retro.moisRetrocession ?
                dayjs(retro.moisRetrocession).format('YYYY-MM') : 'N/A';
            const brancheCode = retro.branche?.codeBranche || 'N/A';
            const key = `${moisRapport}_${brancheCode}`;

            if (!groupes[key]) {
                groupes[key] = {
                    key,
                    moisRapport,
                    branche: retro.branche,
                    retrocessions: [],
                    totalEmissions: 0,
                    totalAnnulations: 0,
                    totalSinistres: 0,
                    totalRecours: 0,
                    totalPenalites: 0,
                    soldeGlobal: 0,
                    nombreCompagnies: 0
                };
            }

            groupes[key].retrocessions.push(retro);
            groupes[key].totalEmissions += retro.totalEmissions || 0;
            groupes[key].totalAnnulations += retro.totalAnnulations || 0;
            groupes[key].totalSinistres += retro.totalSinistres || 0;
            groupes[key].totalRecours += retro.totalRecours || 0;
            groupes[key].totalPenalites += retro.totalPenalites || 0;
            groupes[key].soldeGlobal += retro.soldeGlobal || 0;
            groupes[key].nombreCompagnies = new Set(
                groupes[key].retrocessions.map(r => r.compagnie?.code)
            ).size;
        });

        return Object.values(groupes).sort((a, b) =>
            new Date(b.moisRapport) - new Date(a.moisRapport)
        );
    };

    // Niveau d'affichage au-dessus des groupes mois/branche : une ligne par mois, dépliable par branche
    const regrouperParMois = (groupes) => {
        const mois = {};

        groupes.forEach((groupe) => {
            if (!mois[groupe.moisRapport]) {
                mois[groupe.moisRapport] = {
                    key: groupe.moisRapport,
                    moisRapport: groupe.moisRapport,
                    branches: [],
                    totalEmissions: 0,
                    totalSinistres: 0,
                    totalPenalites: 0,
                    soldeGlobal: 0,
                    nombreCompagnies: 0
                };
            }

            const m = mois[groupe.moisRapport];
            m.branches.push(groupe);
            m.totalEmissions += groupe.totalEmissions;
            m.totalSinistres += groupe.totalSinistres;
            m.totalPenalites += groupe.totalPenalites;
            m.soldeGlobal += groupe.soldeGlobal;
            m.nombreCompagnies = new Set(
                m.branches.flatMap(b => b.retrocessions.map(r => r.compagnie?.code))
            ).size;
        });

        return Object.values(mois).map(m => ({
            ...m,
            branches: m.branches.sort((a, b) =>
                (a.branche?.codeBranche || '').localeCompare(b.branche?.codeBranche || '')
            )
        }));
    };

    const retrocessionsParMois = regrouperParMois(retrocessionsGroupees);

    const genererCompteRetrocession = async (retrocession) => {
        try {
            const response = await axiosInstance.post(
                `/api/retrocession/${retrocession.retrocessionId}/generer-compte-retrocession`,
                {},
                { responseType: 'blob' }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download',
                `Compte_Retrocession_${retrocession.compagnie.raisonSociale}_${retrocession.branche.codeBranche}_${dayjs(retrocession.moisRetrocession).format('YYYY-MM')}.pdf`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success("Document généré avec succès");
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors de la génération du document");
        }
    };

    const genererRecapitulatifGlobal = async (groupe) => {
        try {
            const response = await axiosInstance.post(
                `/api/retrocession/generer-recapitulatif`,
                {
                    mois: groupe.moisRapport + "-01",
                    branche: groupe.branche.codeBranche
                },
                { responseType: 'blob' }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download',
                `Recapitulatif_Retrocession_${groupe.branche.codeBranche}_${groupe.moisRapport}.pdf`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success("Récapitulatif généré avec succès");
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors de la génération du récapitulatif");
        }
    };

    const ouvrirDetails = (retrocession) => {
        setSelectedRetrocession(retrocession);
        setDetailModalVisible(true);
    };

    const ouvrirDetailsGroupe = (retrocession) => {
        console.log("All retro: ", retrocession);
        setSelectedGroup(retrocession);
        setGroupModalVisible(true);
    };





    const telechargerRecapitulatif = async (mois, branche) => {
        try {
            const response = await axiosInstance.post('/api/retrocession/generer-recapitulatif',
                { mois, branche },
                { responseType: 'blob' }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Recapitulatif_${branche}_${mois}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success('Récapitulatif téléchargé avec succès');
        } catch (error) {
            console.error(error);
            toast.error('Erreur lors du téléchargement');
        }
    };

    const telechargerDocument = async (retrocessionId, type) => {
        try {
            const endpoint = type === 'cession'
                ? `/api/retrocession/${retrocessionId}/generer-compte-cession`
                : `/api/retrocession/${retrocessionId}/generer-compte-retrocession`;

            const response = await axiosInstance.post(endpoint, {}, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${type}_${retrocessionId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success('Document téléchargé avec succès');
        } catch (error) {
            console.error(error);
            toast.error('Erreur lors du téléchargement');
        }
    };

    const telechargerBordereau = (dataString, nomFichier) => {
        if (!dataString) {
            toast.warning('Aucune donnée à télécharger');
            return;
        }

        try {
            let parsedData = [];
            if (typeof dataString === 'string') {
                parsedData = JSON.parse(dataString);
            } else {
                parsedData = dataString;
            }

            if (parsedData.length === 0) {
                toast.warning('Aucune donnée à télécharger');
                return;
            }

            const worksheet = XLSX.utils.json_to_sheet(parsedData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Bordereau');
            XLSX.writeFile(workbook, `${nomFichier}.xlsx`);

            toast.success('Bordereau téléchargé avec succès');
        } catch (error) {
            console.error(error);
            toast.error('Erreur lors du téléchargement du bordereau');
        }
    };

    const renderBordereaux = (retrocession) => {
        const bordereaux = [
            {
                titre: 'Émissions',
                data: retrocession.emissionsData,
                nom: `Emissions_${retrocession.compagnie.code}_${dayjs(retrocession.moisRetrocession).format('MM-YYYY')}`,
                icon: <FileExcelOutlined />,
                color: '#52c41a'
            },
            {
                titre: 'Émissions Reportées',
                data: retrocession.emissionsReporteesData,
                nom: `Emissions_Reportees_${retrocession.compagnie.code}_${dayjs(retrocession.moisRetrocession).format('MM-YYYY')}`,
                icon: <FileExcelOutlined />,
                color: '#1890ff'
            },
            {
                titre: 'Annulations',
                data: retrocession.annulationsData,
                nom: `Annulations_${retrocession.compagnie.code}_${dayjs(retrocession.moisRetrocession).format('MM-YYYY')}`,
                icon: <FileExcelOutlined />,
                color: '#faad14'
            },
            {
                titre: 'Sinistres',
                data: retrocession.sinistresData,
                nom: `Sinistres_${retrocession.compagnie.code}_${dayjs(retrocession.moisRetrocession).format('MM-YYYY')}`,
                icon: <FileExcelOutlined />,
                color: '#ff4d4f'
            },
            {
                titre: 'Recours',
                data: retrocession.recoursData,
                nom: `Recours_${retrocession.compagnie.code}_${dayjs(retrocession.moisRetrocession).format('MM-YYYY')}`,
                icon: <FileExcelOutlined />,
                color: '#722ed1'
            },
        ];

        return (
            <Row gutter={[16, 16]} style={{ marginTop: '20px' }}>
                {bordereaux.map((bordereau, index) => {
                    let count = 0;
                    try {
                        const parsed = typeof bordereau.data === 'string'
                            ? JSON.parse(bordereau.data || '[]')
                            : bordereau.data || [];
                        count = parsed.length;
                    } catch (e) {
                        console.error('Erreur parsing:', e);
                    }

                    return (
                        <Col xs={24} sm={12} md={8} lg={8} key={index}>
                            <Card
                                hoverable
                                style={{
                                    borderLeft: `4px solid ${bordereau.color}`,
                                    height: '100%'
                                }}
                                bodyStyle={{ padding: '16px' }}
                            >
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Space>
                                            {bordereau.icon}
                                            <strong>{bordereau.titre}</strong>
                                        </Space>
                                        <Tag color={bordereau.color}>{count} lignes</Tag>
                                    </div>
                                    <Button
                                        type="primary"
                                        icon={<DownloadOutlined />}
                                        onClick={() => telechargerBordereau(bordereau.data, bordereau.nom)}
                                        disabled={count === 0}
                                        style={{ width: '100%', background: bordereau.color, borderColor: bordereau.color }}
                                    >
                                        Télécharger Excel
                                    </Button>
                                </Space>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
        );
    };

    const colonnesPrincipales = [
        {
            title: 'Période',
            dataIndex: 'moisRapport',
            key: 'moisRapport',
            render: (mois) => {
                const [annee, moisNum] = mois.split('-');
                return (
                    <Space>
                        <CalendarOutlined style={{ color: '#1890ff' }} />
                        <span style={{ fontWeight: 600 }}>
                            {frenchMonth(moisNum)} {annee}
                        </span>
                    </Space>
                );
            },
            width: 180,
            sorter: (a, b) => a.moisRapport.localeCompare(b.moisRapport),
        },
        {
            title: 'Branche',
            dataIndex: 'branche',
            key: 'branche',
            render: (branche) => (
                <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                    {branche?.codeBranche || 'N/A'}
                </Tag>
            ),
            width: 120,
        },
        {
            title: 'Compagnies',
            dataIndex: 'nombreCompagnies',
            key: 'nombreCompagnies',
            render: (nombre, record) => (
                <Space wrap size="small">
                    <Tag color="orange">{nombre} compagnie(s)</Tag>
                    {record.retrocessions.slice(0, 3).map(r => (
                        <Tag key={r.retrocessionId} color="default">
                            {r.compagnie?.raisonSociale}
                        </Tag>
                    ))}
                    {record.retrocessions.length > 3 && (
                        <Tag>+{record.retrocessions.length - 3}</Tag>
                    )}
                </Space>
            ),
        },
        {
            title: 'Montant Émission Nette Rétro',
            dataIndex: 'totalEmissions',
            key: 'totalEmissions',
            render: (montant) => (
                <span style={{ color: '#52c41a', fontWeight: 600 }}>
                    {displayNumbers(montant)} F CFA
                </span>
            ),
            align: 'right',
            width: 180,
            sorter: (a, b) => a.totalEmissions - b.totalEmissions,
        },
        {
            title: 'Sinistres Réglés Rétro',
            dataIndex: 'totalSinistres',
            key: 'totalSinistres',
            render: (montant) => (
                <span style={{ color: '#ff4d4f', fontWeight: 600 }}>
                    {displayNumbers(montant)} F CFA
                </span>
            ),
            align: 'right',
            width: 180,
            sorter: (a, b) => a.totalSinistres - b.totalSinistres,
        },
        {
            title: 'Total Pénalités',
            dataIndex: 'totalPenalites',
            key: 'totalPenalites',
            render: (montant) => montant > 0 ? (
                <span style={{ color: '#faad14', fontWeight: 600 }}>
                    {displayNumbers(montant)} F CFA
                </span>
            ) : <span>-</span>,
            align: 'right',
            width: 150,
        },
        {
            title: 'Solde Global',
            dataIndex: 'soldeGlobal',
            key: 'soldeGlobal',
            render: (solde) => {
                const isPositif = solde > 0;
                return (
                    <Tag
                        color={isPositif ? 'red' : 'green'}
                        style={{ fontSize: '13px', fontWeight: 600, padding: '4px 12px' }}
                    >
                        {displayNumbers(Math.abs(solde))} F CFA
                        <br />
                        <small>{isPositif ? 'Compagnie' : 'Pool'}</small>
                    </Tag>
                );
            },
            align: 'center',
            width: 150,
            sorter: (a, b) => a.soldeGlobal - b.soldeGlobal,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space direction="vertical" size="small">
                    <Button
                        type="primary"
                        icon={<FilePdfOutlined />}
                        onClick={() => genererRecapitulatifGlobal(record)}
                        size="small"
                        style={{ width: '100%' }}
                    >
                        Récapitulatif Global
                    </Button>
                    <Button
                        type="default"
                        icon={<EyeOutlined />}
                        onClick={() => { ouvrirDetailsGroupe(record); }}
                        size="small"
                        style={{ width: '100%' }}
                    >
                        Voir Détails
                    </Button>
                </Space>
            ),
            align: 'center',
            fixed: 'right',
            width: 180,
        },
    ];

    // Sous-tableau d'un mois : la période est déjà portée par la ligne du mois
    const colonnesBranches = colonnesPrincipales.filter(c => c.key !== 'moisRapport');

    const colonneParKey = (key) => colonnesPrincipales.find(c => c.key === key);

    const colonnesMois = [
        colonneParKey('moisRapport'),
        {
            title: 'Branches',
            key: 'branches',
            render: (_, record) => (
                <Space wrap size="small">
                    {record.branches.map(b => (
                        <Tag key={b.key} color="blue">
                            {b.branche?.codeBranche || 'N/A'}
                        </Tag>
                    ))}
                </Space>
            ),
            width: 200,
        },
        {
            title: 'Compagnies',
            dataIndex: 'nombreCompagnies',
            key: 'nombreCompagnies',
            render: (nombre) => <Tag color="orange">{nombre} compagnie(s)</Tag>,
        },
        ...['totalEmissions', 'totalSinistres', 'totalPenalites', 'soldeGlobal'].map(colonneParKey),
    ];

    const colonnesDetail = [
        {
            title: 'Compagnie',
            dataIndex: 'compagnie',
            key: 'compagnie',
            render: (compagnie) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{compagnie?.raisonSociale}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{compagnie?.code}</div>
                </div>
            ),
        },
        {
            title: 'Émissions Rétro',
            dataIndex: 'totalEmissions',
            key: 'totalEmissions',
            render: (montant) => (
                <span style={{ color: '#52c41a' }}>
                    {displayNumbers(montant || 0)} F CFA
                </span>
            ),
            align: 'right',
        },
        {
            title: 'Sinistres Rétro',
            dataIndex: 'totalSinistres',
            key: 'totalSinistres',
            render: (montant) => (
                <span style={{ color: '#ff4d4f' }}>
                    {displayNumbers(montant || 0)} F CFA
                </span>
            ),
            align: 'right',
        },
        {
            title: 'Pénalité',
            dataIndex: 'penalite',
            key: 'penalite',
            render: (penalite, record) => penalite > 0 ? (
                <Tooltip title={`Retard: ${record.joursRetard} jour(s)`}>
                    <span style={{ color: '#faad14', fontWeight: 600 }}>
                        {displayNumbers(penalite)} F CFA
                    </span>
                </Tooltip>
            ) : <span>-</span>,
            align: 'right',
        },
        {
            title: 'Solde',
            dataIndex: 'soldeGlobal',
            key: 'soldeGlobal',
            render: (solde) => {
                const isPositif = solde > 0;
                return (
                    <Tag color={isPositif ? 'red' : 'green'}>
                        {displayNumbers(Math.abs(solde))} F
                        <br />
                        {isPositif ? 'Compagnie' : 'Pool'}
                    </Tag>
                );
            },
            align: 'center',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Compte de Rétrocession">
                        <Button
                            type="primary"
                            icon={<FilePdfOutlined />}
                            onClick={() => genererCompteRetrocession(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Voir Détails">
                        <Button
                            type="default"
                            icon={<EyeOutlined />}
                            onClick={() => ouvrirDetails(record)}
                            size="small"
                        />
                    </Tooltip>
                </Space>
            ),
            align: 'center',
            width: 120,
        },
    ];

    const expandedRowRender = (record) => {
        return (
            <div style={{ padding: '16px', background: '#fafafa', borderRadius: '8px' }}>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={6}>
                        <Statistic
                            title="Réserve de Garantie (5%)"
                            value={displayNumbers(record.totalEmissions * 0.05)}
                            suffix="F CFA"
                            valueStyle={{ fontSize: '14px', color: '#1890ff' }}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="Frais Admin Pool (6%)"
                            value={displayNumbers(record.totalEmissions * 0.06)}
                            suffix="F CFA"
                            valueStyle={{ fontSize: '14px', color: '#fa8c16' }}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="Prévention Routière (1%)"
                            value={displayNumbers(record.totalEmissions * 0.01)}
                            suffix="F CFA"
                            valueStyle={{ fontSize: '14px', color: '#52c41a' }}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="Indemnité Gestion (15%)"
                            value={displayNumbers(record.totalEmissions * 0.15)}
                            suffix="F CFA"
                            valueStyle={{ fontSize: '14px', color: '#722ed1' }}
                        />
                    </Col>
                </Row>

                <Divider />

                <h4 style={{ marginBottom: '16px', color: '#1890ff' }}>
                    Détails par Compagnie ({record.retrocessions.length})
                </h4>
                <Table
                    columns={colonnesDetail}
                    dataSource={record.retrocessions}
                    pagination={false}
                    rowKey="retrocessionId"
                    size="small"
                    bordered
                />
            </div>
        );
    };

    const expandedMoisRender = (mois) => (
        <Table
            columns={colonnesBranches}
            dataSource={mois.branches}
            expandable={{
                expandedRowRender,
                rowExpandable: (record) => record.retrocessions.length > 0,
            }}
            pagination={false}
            size="small"
            scroll={{ x: 1200 }}
        />
    );

    const calculerStatistiquesGlobales = () => {
        return {
            totalRetrocessions: retrocessions.length,
            totalEmissions: retrocessions.reduce((sum, r) => sum + (r.totalEmissions || 0), 0),
            totalSinistres: retrocessions.reduce((sum, r) => sum + (r.totalSinistres || 0), 0),
            totalPenalites: retrocessions.reduce((sum, r) => sum + (r.penalite || 0), 0),
        };
    };

    const stats = calculerStatistiquesGlobales();

    return (
        <div className="layout-wrapper layout-content-navbar">
            <style>{`
        .retro-header-card {
          background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
          border-radius: 16px;
          padding: 32px;
          margin-bottom: 24px;
          box-shadow: 0 4px 20px rgba(24, 144, 255, 0.25);
          color: white;
        }

        .retro-header-card h2 {
          color: white;
          font-size: 28px;
          font-weight: 700;
          margin: 0 0 8px 0;
        }

        .filter-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 24px;
        }

        .stats-card {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 24px;
        }
      `}</style>

            <div className="layout-container">
                <SideBar />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper" style={{ background: '#f5f5f5' }}>
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div className="retro-header-card">
                                <h2>
                                    <FileTextOutlined /> Consultation des Rétrocessions
                                </h2>
                                <p>Suivi et génération des documents de rétrocession par catégorie et compagnie</p>
                            </div>

                            {/* Statistiques */}
                            {retrocessions.length > 0 && (
                                <div className="stats-card">
                                    <Row gutter={16}>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Total Rétrocessions"
                                                value={stats.totalRetrocessions}
                                                prefix={<FileTextOutlined />}
                                                valueStyle={{ color: '#1890ff' }}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Total Émissions Rétro"
                                                value={displayNumbers(stats.totalEmissions)}
                                                suffix="F CFA"
                                                valueStyle={{ color: '#52c41a', fontSize: '18px' }}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Total Sinistres Rétro"
                                                value={displayNumbers(stats.totalSinistres)}
                                                suffix="F CFA"
                                                valueStyle={{ color: '#ff4d4f', fontSize: '18px' }}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Total Pénalités"
                                                value={displayNumbers(stats.totalPenalites)}
                                                suffix="F CFA"
                                                valueStyle={{ color: '#faad14', fontSize: '18px' }}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* Filtres */}
                            <div className="filter-card">
                                <Row gutter={16}>
                                    <Col xs={24} sm={8} md={6}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            Période
                                        </label>
                                        <DatePicker
                                            placeholder="Filtrer par mois"
                                            onChange={(_, dateString) => setSelectedPeriode(dateString)}
                                            picker="month"
                                            style={{ width: '100%' }}
                                        />
                                    </Col>

                                    <Col xs={24} sm={8} md={6}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            Branche
                                        </label>
                                        <Select
                                            placeholder="Toutes les catégories"
                                            style={{ width: '100%' }}
                                            onChange={setSelectedBranche}
                                            allowClear
                                            options={branches.map(b => ({
                                                value: b.codeBranche,
                                                label: b.nom
                                            }))}
                                        />
                                    </Col>

                                    {isPoolUser && (
                                        <Col xs={24} sm={8} md={6}>
                                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                                Compagnie
                                            </label>
                                            <Select
                                                placeholder="Toutes les compagnies"
                                                style={{ width: '100%' }}
                                                onChange={setSelectedCompagnie}
                                                allowClear
                                                options={compagnies.map(c => ({
                                                    value: c.code,
                                                    label: c.raisonSociale
                                                }))}
                                            />
                                        </Col>
                                    )}

                                    <Col xs={24} sm={8} md={6}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            &nbsp;
                                        </label>
                                        <Button
                                            type="primary"
                                            icon={<ReloadOutlined />}
                                            onClick={chargerRetrocessions}
                                            style={{ width: '100%' }}
                                            loading={loading}
                                        >
                                            Actualiser
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            {/* Table */}
                            <Card
                                title="Rétrocessions par Mois"
                                style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}
                            >
                                <ConfigProvider locale={frFR}>
                                    <Table
                                        columns={colonnesMois}
                                        dataSource={retrocessionsParMois}
                                        loading={loading}
                                        expandable={{
                                            expandedRowRender: expandedMoisRender,
                                            rowExpandable: (record) => record.branches.length > 0,
                                        }}
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showTotal: (total) => `Total: ${total} période(s)`,
                                        }}
                                        scroll={{ x: 1400 }}
                                    />
                                </ConfigProvider>
                            </Card>

                        </div>

                        <Footer />
                    </div>
                </div>
            </div>

            {/* Modal de détails */}
            <Modal
                title={
                    selectedRetrocession && (
                        <span>
                            <BankOutlined style={{ marginRight: 8 }} />
                            Détails Rétrocession - {selectedRetrocession.compagnie?.raisonSociale}
                        </span>
                    )
                }
                open={detailModalVisible}
                onCancel={() => {
                    setDetailModalVisible(false);
                    setSelectedRetrocession(null);
                }}
                width={800}
                zIndex={3000}
                footer={[
                    <Button key="close" onClick={() => setDetailModalVisible(false)}>
                        Fermer
                    </Button>,
                    <Button
                        key="download"
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => genererCompteRetrocession(selectedRetrocession)}
                    >
                        Télécharger PDF
                    </Button>
                ]}
            >
                {selectedRetrocession && (
                    <Descriptions bordered column={2}>
                        <Descriptions.Item label="Compagnie" span={2}>
                            {selectedRetrocession.compagnie?.raisonSociale}
                        </Descriptions.Item>
                        <Descriptions.Item label="Branche">
                            <Tag color="blue">{selectedRetrocession.branche?.codeBranche}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Période">
                            {dayjs(selectedRetrocession.moisRetrocession).format('MMMM YYYY')}
                        </Descriptions.Item>
                        <Descriptions.Item label="Émissions Nette Rétro">
                            <span style={{ color: '#52c41a', fontWeight: 600 }}>
                                {displayNumbers(selectedRetrocession.totalEmissions)} F CFA
                            </span>
                        </Descriptions.Item>
                        <Descriptions.Item label="Sinistres Réglés Rétro">
                            <span style={{ color: '#ff4d4f', fontWeight: 600 }}>
                                {displayNumbers(selectedRetrocession.totalSinistres)} F CFA
                            </span>
                        </Descriptions.Item>
                        <Descriptions.Item label="Indemnité Gestion (15%)">
                            {displayNumbers(selectedRetrocession.totalEmissions * 0.15)} F CFA
                        </Descriptions.Item>
                        <Descriptions.Item label="Frais Admin Pool (6%)">
                            {displayNumbers(selectedRetrocession.totalEmissions * 0.06)} F CFA
                        </Descriptions.Item>
                        <Descriptions.Item label="Prévention Routière (1%)">
                            {displayNumbers(selectedRetrocession.totalEmissions * 0.01)} F CFA
                        </Descriptions.Item>
                        <Descriptions.Item label="Réserve Garantie (5%)">
                            {displayNumbers(selectedRetrocession.totalEmissions * 0.05)} F CFA
                        </Descriptions.Item>
                        {selectedRetrocession.penalite > 0 && (
                            <>
                                <Descriptions.Item label="Jours de Retard">
                                    <Tag color="red">{selectedRetrocession.joursRetard} jour(s)</Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Pénalité">
                                    <span style={{ color: '#faad14', fontWeight: 600 }}>
                                        {displayNumbers(selectedRetrocession.penalite)} F CFA
                                    </span>
                                </Descriptions.Item>
                            </>
                        )}
                        <Descriptions.Item label="Solde" span={2}>
                            {selectedRetrocession.soldeGlobal > 0 ? (
                                <Tag color="red" style={{ fontSize: '16px', padding: '8px 16px' }}>
                                    {displayNumbers(selectedRetrocession.soldeGlobal - (selectedRetrocession.totalEmissions * 0.27 + selectedRetrocession.totalSinistres))} F CFA - En faveur Compagnie
                                </Tag>
                            ) : (
                                <Tag color="green" style={{ fontSize: '16px', padding: '8px 16px' }}>
                                    {displayNumbers(Math.abs(selectedRetrocession.soldeGlobal - (selectedRetrocession.totalEmissions * 0.27 + selectedRetrocession.totalSinistres)))} F CFA - En faveur Pool
                                </Tag>
                            )}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
            {/* Modal de détails */}
            <Modal
                title={
                    selectedGroup && (
                        <span>
                            Détails Rétrocession - {selectedGroup.branche?.codeBranche} - {' '}
                            {frenchMonth(selectedGroup?.moisRapport?.split('-')[1])} {selectedGroup?.moisRapport?.split('-')[0]}
                        </span>
                    )
                }
                open={groupModalVisible}
                onCancel={() => {
                    setGroupModalVisible(false);
                    setSelectedGroup(null);
                }}
                width="95%"
                style={{ top: 20 }}
                footer={null}
                zIndex={3000}
            >
                {selectedGroup && (
                    <ConfigProvider locale={frFR}>
                        <Tabs
                            type="card"
                            items={[
                                {
                                    label: (
                                        <span>
                                            <TableOutlined /> Tableau Récapitulatif
                                        </span>
                                    ),
                                    key: "1",
                                    children: (
                                        <TableauRecapitulatifRetrocession selectedGroup={selectedGroup} />
                                    ),
                                },
                                ...Object.values(selectedGroup.retrocessions ?? {})
                                    .sort((a, b) => a.compagnie.code.localeCompare(b.compagnie.code, undefined, { numeric: true }))
                                    .map((c, index) => ({
                                        label: (
                                            <span>
                                                {c.compagnie.raisonSociale}
                                                {c.numeroCheque && c.banque && (
                                                    <Tag color="green" style={{ marginLeft: 8, fontSize: 10 }}>
                                                        <CheckOutlined /> Chèque
                                                    </Tag>
                                                )}
                                            </span>
                                        ),
                                        key: `compagnie-${index}`,
                                        children: (
                                            <div>
                                                <Divider>Informations de la compagnie</Divider>
                                                <Row gutter={16} style={{ marginBottom: '20px' }}>
                                                    <Col span={12}>
                                                        <Statistic
                                                            title="Émissions"
                                                            value={displayNumbers(c.totalEmissions || 0)}
                                                            suffix="F CFA"
                                                            valueStyle={{ color: '#52c41a' }}
                                                        />
                                                    </Col>
                                                    <Col span={12}>
                                                        <Statistic
                                                            title="Sinistres"
                                                            value={displayNumbers(c.totalSinistres || 0)}
                                                            suffix="F CFA"
                                                            valueStyle={{ color: '#ff4d4f' }}
                                                        />
                                                    </Col>
                                                </Row>

                                                {/* Section Chèque : le Pool ASSIGNE le chèque ; la compagnie en ACCUSE RÉCEPTION. */}
                                                <ChequeRetrocession
                                                    retrocession={c}
                                                    onUpdate={chargerRetrocessions}
                                                    isPool={isPoolUser}
                                                />

                                                <Divider>Documents PDF</Divider>
                                                <Space style={{ marginBottom: '20px' }}>
                                                    <Button
                                                        type="primary"
                                                        icon={<FilePdfOutlined />}
                                                        onClick={() => telechargerDocument(c.retrocessionId, 'cession')}
                                                    >
                                                        Compte de Cession
                                                    </Button>
                                                    <Button
                                                        type="primary"
                                                        icon={<FilePdfOutlined />}
                                                        onClick={() => telechargerDocument(c.retrocessionId, 'retrocession')}
                                                        style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                                    >
                                                        Compte de Rétrocession
                                                    </Button>
                                                </Space>
                                            </div>
                                        ),
                                    })),
                                // --- Nouvel onglet unique : Bordereaux Détaillés ---
                                {
                                    label: (
                                        <span>
                                            <FileExcelOutlined /> Bordereaux Détaillés
                                        </span>
                                    ),
                                    key: 'bordereaux',
                                    children: (
                                        <BordereauxDetailles selectedGroup={selectedGroup} />
                                    ),
                                },
                            ]}
                        />
                    </ConfigProvider>
                )}
            </Modal>

            <div className="layout-overlay layout-menu-toggle"></div>
            {LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL" && (
                <div class="buy-now">
                    <button
                        onClick={() => navigate('/retrocession')}
                        class="btn btn-secondary btn-buy-now"
                        style={{
                            backgroundColor: '#242166',
                            borderColor: '#242166',
                            fontWeight: 500,
                            fontSize: '15px',
                            padding: '9px 24px',
                            borderRadius: '10px',
                            boxShadow: '0 6px 20px rgba(36, 33, 102, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <CheckOutlined style={{ fontSize: '18px' }} />
                        Effectuer une rétrocession
                    </button>
                </div>
            )}
            <div className="drag-target"></div>
        </div>
    );
};

export default Retrocessions;